import React, { useState, useMemo, useEffect, useCallback, useContext, useLayoutEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import styled from '@emotion/styled/macro';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import SettersContext from '../Contexts/SettersContext';
import ContactDirectoryListItem from './ContactDirectoryListItem';

const StyledTreeView = styled(SimpleTreeView)`
  && .MuiTreeItem-content {
    padding: 0px 1rem;
  }

  && .MuiTreeItem-label {
    margin: 0.2rem 0;
    font-weight: 700;
  }

  .MuiTreeItem-groupTransition {
    margin-left: calc(1.5rem - 1px);
    border-left: 2px solid #f5f5f5;
  }

  && > li > .MuiTreeItem-content {
    padding: 0 1rem;
  }

  && > li > .MuiTreeItem-groupTransition {
    margin-left: calc(1rem + 0.5rem - 1px);
  }
`;

const UnpaddedList = styled(List)`
  padding: 0;
`;

const UNCATEGORIZED_KEY = '$items';

const collectAllIds = (nodes, parentId = 'root') => {
  const ids = [];
  Object.keys(nodes || {})
    .filter(key => key !== UNCATEGORIZED_KEY)
    .forEach(key => {
      const id = `${parentId}.${key}`;
      ids.push(id);
      ids.push(...collectAllIds(nodes[key], id));
    });
  if (nodes?.[UNCATEGORIZED_KEY]?.length) {
    ids.push(`${parentId}.${UNCATEGORIZED_KEY}`);
  }

  return ids;
};

const renderItems = ($items) => (
  <UnpaddedList>
    {$items.map(({ type, id }) => <ContactDirectoryListItem key={type + id} itemType={type} itemId={id} />)}
  </UnpaddedList>
);

const RenderNodes = ({ nodes, parentId, depth, t }) => {
  const childKeys = Object.keys(nodes || {})
    .filter(key => key !== UNCATEGORIZED_KEY)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  const $items = nodes?.[UNCATEGORIZED_KEY];
  const $itemsId = `${parentId}.${UNCATEGORIZED_KEY}`;

  return (
    <>
      {childKeys.map((key) => {
        const id = `${parentId}.${key}`;
        return (
          <TreeItem itemId={id} key={id} label={key}>
            <RenderNodes nodes={nodes[key]} parentId={id} depth={depth + 1} t={t} />
          </TreeItem>
        );
      })}
      {$items?.length && depth === 0 && (
        <TreeItem itemId={$itemsId} key={$itemsId} label={t('component:contactDirectory.uncategorized')}>
          {renderItems($items)}
        </TreeItem>
      )}
      {$items?.length && depth !== 0 && renderItems($items)}
    </>
  );
};

RenderNodes.propTypes = {
  nodes: PropTypes.object.isRequired,
  parentId: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

const ContactDirectoryTree = ({ items, ...rest }) => {

  const { t } = useTranslation(['component']);

  const { setButtons } = useContext(SettersContext);

  const [preferCollapsed, setPreferCollapsed] = useState(false);
  const [userExpandedItems, setUserExpandedItems] = useState(null);

  const allIds = useMemo(() => collectAllIds(items), [items]);

  const expandedItems = userExpandedItems ?? (preferCollapsed ? [] : allIds);

  useEffect(() => {
    setUserExpandedItems(null);
  }, [preferCollapsed]);

  const onExpandedItemsChange = (e, ids) => {
    setUserExpandedItems(ids);
  };

  const renderButton = useCallback(() => {
    const onClick = () => {
      setPreferCollapsed(current => !current);
    };

    const title = preferCollapsed === false ? t('component:contactDirectory.collapseAllButton') : t('component:contactDirectory.expandAllButton');
    const Icon = preferCollapsed === false ? UnfoldLessIcon : UnfoldMoreIcon;

    setButtons(<IconButton fontSize="small" onClick={onClick} title={title} aria-label={title}><Icon /></IconButton>);
  }, [preferCollapsed, setButtons, t]);

  useLayoutEffect(() => {
    renderButton();
    return () => setButtons(null);
  }, [renderButton, setButtons]);

  return (
    <InfiniteScroll key={preferCollapsed ? 'collapsed' : 'not-collapsed'} {...rest}>
      <StyledTreeView
        disableSelection
        disabledItemsFocusable={false}
        expandedItems={expandedItems}
        onExpandedItemsChange={onExpandedItemsChange}
      >
        <RenderNodes nodes={items} parentId="root" depth={0} t={t} />
      </StyledTreeView>
    </InfiniteScroll>
  );
};

ContactDirectoryTree.propTypes = {
  items: PropTypes.object.isRequired,
};

export default ContactDirectoryTree;
